import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaSearch,
  FaTimesCircle,
} from "react-icons/fa";

import PartnerSidebar from "../PartnerSidebar";
import "./Orders.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ORDERS_PER_PAGE = 20;

const EMPTY_SUMMARY = { total: 0, pending: 0, completed: 0, cancelled: 0 };

const PartnerOrders = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [isLoading, setIsLoading] = useState(true);

  // Wait for the user to stop typing before searching.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Any filter change starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, fromDate, toDate]);

  useEffect(() => {
    let mounted = true;

    const loadOrders = async () => {
      setIsLoading(true);

      try {
        const params = { page, limit: ORDERS_PER_PAGE };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== "All") params.status = statusFilter;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;

        const response = await apiClient.get("partner/orders", { params });
        if (!mounted) return;

        const data = response.data?.data || {};
        setOrders(Array.isArray(data.orders) ? data.orders : []);
        setSummary(data.summary || EMPTY_SUMMARY);
        setPagination(data.pagination || { total: 0, totalPages: 1 });
      } catch (error) {
        if (!mounted) return;

        toast.error(
          error.response?.data?.message || "Unable to load orders",
          { position: "top-center" }
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadOrders();

    return () => {
      mounted = false;
    };
  }, [page, debouncedSearch, statusFilter, fromDate, toDate]);

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("All");
    setFromDate("");
    setToDate("");
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatAmount = (amount) =>
    `₹${Number(amount).toLocaleString("en-IN")}`;

  const getStatusIcon = (status) => {
    if (status === "Completed") return <FaCheckCircle />;
    if (status === "Cancelled") return <FaTimesCircle />;
    return <FaClock />;
  };

  return (
    <div className="partner-orders-layout">
      <PartnerSidebar />

      <main className="partner-orders-main">
        <div className="partner-orders-container">
          <header className="orders-page-header">
            <div>
              <h1>Orders</h1>
              <p>
                Track which product was ordered, quantity, buyer, order date,
                and current status.
              </p>
            </div>
          </header>

          <section className="orders-summary-grid">
            <SummaryCard label="Total Orders" value={summary.total} />
            <SummaryCard label="Pending" value={summary.pending} />
            <SummaryCard label="Completed" value={summary.completed} />
            <SummaryCard label="Cancelled" value={summary.cancelled} />
          </section>

          <section className="orders-card orders-filter-card">
            <div className="orders-filter-header">
              <div>
                <h2>Search & Filters</h2>
                <p>Filter orders by status, date, order ID, product, or buyer.</p>
              </div>

              <button type="button" className="orders-clear-button" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>

            <div className="orders-filter-grid">
              <label className="orders-search-field">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Search order ID, product or buyer..."
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </label>

              <label className="orders-filter-field">
                <span>Status</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </label>

              <label className="orders-filter-field">
                <span>From Date</span>
                <div className="orders-date-input">
                  <FaCalendarAlt />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                  />
                </div>
              </label>

              <label className="orders-filter-field">
                <span>To Date</span>
                <div className="orders-date-input">
                  <FaCalendarAlt />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                  />
                </div>
              </label>
            </div>
          </section>

          <section className="orders-card orders-table-card">
            <div className="orders-table-header">
              <div>
                <h2>Order List</h2>
                <p>Showing {orders.length} of {pagination.total} orders</p>
              </div>
            </div>

            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Buyer</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {orders.length > 0 ? (
                    orders.map((order) => (
                      <tr key={order.id}>
                        <td><strong className="order-id">{order.orderCode}</strong></td>
                        <td>
                          <div className="order-product-cell">
                            <span className="order-product-icon"><FaBoxOpen /></span>
                            <strong>{order.product}</strong>
                          </div>
                        </td>
                        <td>{order.quantity}</td>
                        <td>{order.buyer}</td>
                        <td>{formatDate(order.date)}</td>
                        <td><strong className="order-amount">{formatAmount(order.amount)}</strong></td>
                        <td>
                          <span className={`order-status ${order.status.toLowerCase()}`}>
                            {getStatusIcon(order.status)}
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="orders-empty-state">
                          <FaBoxOpen />
                          <strong>{isLoading ? "Loading orders..." : "No orders found"}</strong>
                          {!isLoading && <p>Change the filters to view matching orders.</p>}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="orders-pagination">
                <button
                  type="button"
                  className="orders-clear-button"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </button>
                <span>
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className="orders-clear-button"
                  disabled={page >= pagination.totalPages || isLoading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <article className="orders-summary-card">
    <span>{label}</span>
    <strong>{value}</strong>
  </article>
);

export default PartnerOrders;